import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, Users, Flag, MapPin } from 'lucide-react';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const Signup = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || '/account';
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirm: ''
  });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await register(form.email, form.password, form.name, form.phone);
      toast.success('Welcome to the Rally Family!');
      navigate(redirectTo);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="signup-page" className="min-h-screen bg-[#0A0A0A]">
      <Navigation />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Why account */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-28"
          >
            <h1 className="font-['Oswald'] text-4xl sm:text-5xl text-[#F2EDE6] mb-6">
              JOIN THE PACK.
            </h1>
            <p className="text-[#A19D98] text-lg leading-relaxed mb-8">
              We want you to keep coming back. Creating an account lets us track your rally
              history, save your vehicles, and build the Revline Rally family the way we
              always intended — with people who show up, ride hard, and stick around.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#E8651A]/10 border border-[#E8651A]/30">
                  <Users className="w-5 h-5 text-[#E8651A]" />
                </div>
                <div>
                  <h3 className="font-['Oswald'] text-[#F2EDE6] tracking-wider">BECOME A REGULAR</h3>
                  <p className="text-[#A19D98] text-sm">
                    We keep track of who rolls with us. Recurring members get priority, early access, and insider perks.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#E8651A]/10 border border-[#E8651A]/30">
                  <Flag className="w-5 h-5 text-[#E8651A]" />
                </div>
                <div>
                  <h3 className="font-['Oswald'] text-[#F2EDE6] tracking-wider">YOUR GARAGE, SAVED</h3>
                  <p className="text-[#A19D98] text-sm">
                    Add every car in your garage once. Pick the one you're bringing each rally in a single click.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#E8651A]/10 border border-[#E8651A]/30">
                  <MapPin className="w-5 h-5 text-[#E8651A]" />
                </div>
                <div>
                  <h3 className="font-['Oswald'] text-[#F2EDE6] tracking-wider">TRACK EVERY RALLY</h3>
                  <p className="text-[#A19D98] text-sm">
                    View your registrations, past rallies, and payment history in one dashboard.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Signup form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-[#141414] border border-[#2A2A2A] p-8"
          >
            <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6] tracking-wider mb-6">
              CREATE YOUR ACCOUNT
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Full Name *</label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  data-testid="signup-name-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  data-testid="signup-email-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Phone</label>
                <Input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="signup-phone-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Password * (min 8 chars)</label>
                <Input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  minLength={8}
                  required
                  data-testid="signup-password-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <div>
                <label className="block text-[#A19D98] text-sm mb-2">Confirm Password *</label>
                <Input
                  type="password"
                  name="confirm"
                  value={form.confirm}
                  onChange={handleChange}
                  required
                  data-testid="signup-confirm-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="signup-submit-btn"
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Create Account'}
              </button>

              <p className="text-[#A19D98] text-sm text-center">
                Already have an account?{' '}
                <Link to="/login" state={{ redirectTo }} className="text-[#E8651A] hover:underline">
                  Log in
                </Link>
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Signup;
